/**
 * React Query hook for fetching application configuration
 * Provides available GPT models, indexes, prompts, etc.
 */

import { useQuery } from "@tanstack/react-query"

import type { Config } from "@/api/models"
import type { SessionType } from "@/types"

import { configApi } from "@/api/api"
import { isHostedAgentsEnabledInConfig } from "@/hooks/feature-utils"
import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"

function fetchConfig(): Promise<Config> {
  const settingsService = SettingsService.getInstance()
  return configApi(settingsService.token)
}

export function useConfigQuery<TData = Config>(options?: {
  select?: (data: Config) => TData
  enabled?: boolean
}) {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: fetchConfig,
    staleTime: 10 * 60 * 1000, // 10 minutes - config rarely changes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Config rarely changes, no need to refetch on focus
    // Bootstrap query — see the note in use-user-settings.ts. A `navigator.onLine`
    // false negative must not pause this before it reaches the API.
    networkMode: "always",
    select: options?.select,
    // `fetchConfig` reads the settings singleton, so callers that render during
    // bootstrap gate the query until that singleton exists.
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook to get available GPT models for a specific session type
 * Uses select to prevent re-renders when config changes but filtered models don't
 */
export function useAvailableGPTModels(sessionType: SessionType) {
  const {
    data: availableModels,
    isError,
    isLoading,
  } = useConfigQuery({
    select: (config) => {
      const models = config.models
      return models.filter(
        (model) =>
          (!model.requiredModules ||
            model.requiredModules.length === 0 ||
            model.requiredModules.includes(sessionType)) &&
          (!model.blockedModules || !model.blockedModules.includes(sessionType))
      )
    },
  })

  return {
    models: availableModels || [],
    allModels: availableModels || [],
    isError,
    isLoading,
  }
}

/**
 * Hook to get all available settings from config
 */
export function useAvailableSettings(sessionType: SessionType) {
  const { data: config, isLoading: configLoading } = useConfigQuery()
  const { models: availableModels, isLoading: modelsLoading } = useAvailableGPTModels(sessionType)

  return {
    availableGptModels: availableModels,
    availableFeatures: config?.features || [],
    isLoading: configLoading || modelsLoading,
  }
}

/**
 * Returns true when the user has access to the admin dashboard.
 * Used to gate admin-only controls like the tender model selector.
 */
export function useTenderModelOverrideEnabled(): boolean {
  const { data: config } = useConfigQuery()
  return config?.frontend.routes.some((r) => r.id === "admin-dashboard") ?? false
}

/**
 * Whether hosted agents are offered on this deployment (`/api/config`
 * `hostedAgentsEnabled`). False while config is loading and whenever the key
 * is missing — fail closed, never flash hosted-agent UI.
 */
export function useHostedAgentsEnabled(): boolean {
  const { data } = useConfigQuery({ select: isHostedAgentsEnabledInConfig })
  return data ?? false
}
